import React, { useState, useEffect, useRef, useCallback } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import viewImage from "../../assets/images/icons/view.svg";
import FilterTrigger from "../../commons/FilterTrigger/FilterTrigger";
import FilterOptionsMenu from "../../commons/FilterOptionsMenu/FilterOptionsMenu";
import { Link, useLocation } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import TablePagination from "../../commons/Pagination/TablePagination";
import { UI_CONSTANTS } from "../../utils/constants";
import { applyListState } from "../../utils/listNavigation";

const PAYMENT_STATUS = {
  UNPAID: 0,
  PAID: 1,
  REFUNDED: 2,
};

const PAYMENT_STATUS_LABELS = {
  [PAYMENT_STATUS.UNPAID]: "Unpaid",
  [PAYMENT_STATUS.PAID]: "Paid",
  [PAYMENT_STATUS.REFUNDED]: "Refunded",
};

const PAYMENT_STATUS_FILTER_OPTIONS = ["All", "Unpaid", "Paid", "Refunded"];

const PAYMENT_STATUS_FILTER_MAP = {
  Unpaid: PAYMENT_STATUS.UNPAID,
  Paid: PAYMENT_STATUS.PAID,
  Refunded: PAYMENT_STATUS.REFUNDED,
};

const SUBSCRIPTION_STATUS_FILTER_OPTIONS = [
  "All",
  "Pending",
  "Active",
  "Exhausted",
  "Expired",
  "Cancelled",
];

const SUBSCRIPTION_STATUS_FILTER_MAP = {
  Pending: "PENDING",
  Active: "ACTIVE",
  Exhausted: "EXHAUSTED",
  Expired: "EXPIRED",
  Cancelled: "CANCELLED",
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatAmount = (item) => {
  const amount = item?.amount ?? item?.payment ?? item?.price ?? item?.total_amount;
  if (amount === undefined || amount === null || amount === "") return "N/A";
  const currency = String(item?.currency || "gbp").toUpperCase();
  const symbol = currency === "GBP" ? "£" : currency === "USD" ? "$" : currency === "EUR" ? "€" : "";
  return `${symbol}${amount}`;
};

const formatBillingCycle = (item) => {
  const cycle =
    item?.subscription_plan_id?.billing_cycle ||
    item?.billing_cycle ||
    item?.plan_type ||
    item?.planType;
  if (!cycle) return "N/A";
  if (cycle === "one_time") {
    const months =
      item?.subscription_plan_id?.plan_duration_months || item?.plan_duration_months;
    return months ? `${months} Month${months > 1 ? "s" : ""}` : "One Time";
  }
  return String(cycle)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getPaymentStatusLabel = (status) => {
  if (status === undefined || status === null || status === "") return "N/A";
  const numeric = Number(status);
  if (!Number.isNaN(numeric) && PAYMENT_STATUS_LABELS[numeric]) {
    return PAYMENT_STATUS_LABELS[numeric];
  }
  const normalized = String(status).toUpperCase();
  if (normalized === "UNPAID") return "Unpaid";
  if (normalized === "PAID") return "Paid";
  if (normalized === "REFUNDED") return "Refunded";
  return String(status);
};

const getSubscriptionStatusLabel = (status) => {
  if (status === undefined || status === null || status === "") return "N/A";
  const normalized = String(status).toUpperCase();
  const labels = {
    PENDING: "Pending",
    ACTIVE: "Active",
    EXHAUSTED: "Exhausted",
    EXPIRED: "Expired",
    CANCELLED: "Cancelled",
    CANCELED: "Cancelled",
  };
  return labels[normalized] || String(status);
};

const parseTransactionList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.transaction)) return payload.transaction;
  if (Array.isArray(payload?.transactions)) return payload.transactions;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result)) return payload.result;
  if (Array.isArray(payload?.list)) return payload.list;
  return [];
};

const getRestaurantName = (item) =>
  item?.restaurant_id?.restaurant_name ||
  item?.restaurant_name ||
  item?.restaurant?.restaurant_name ||
  item?.restaurant_id?.full_name ||
  "N/A";

const getSubscriptionName = (item) =>
  item?.plan_name ||
  item?.subscription_plan_id?.name ||
  item?.subscription ||
  item?.subscription_name ||
  "N/A";

const getPlanType = (item) => {
  if (item?.plan_type || item?.planType) return item.plan_type || item.planType;
  if (item?.subscription_plan_id?.is_recurring === true) return "Recurring";
  if (item?.subscription_plan_id?.is_recurring === false) return "One Time";
  if (item?.subscription_plan_id?.billing_cycle === "one_time") return "One Time";
  return "N/A";
};

function TransactionManagement() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [paymentFilter, setPaymentFilter] = useState("All");
  const [subscriptionFilter, setSubscriptionFilter] = useState("All");
  const [showPaymentFilter, setShowPaymentFilter] = useState(false);
  const [showSubscriptionFilter, setShowSubscriptionFilter] = useState(false);
  const paymentFilterRef = useRef(null);
  const subscriptionFilterRef = useRef(null);
  const restoredRef = useRef(false);

  const getTransactionList = useCallback(async () => {
    setLoading(true);
    try {
      const trimmedSearch = search.trim();
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (trimmedSearch) {
        params.set("search", trimmedSearch);
      }

      if (paymentFilter !== "All" && PAYMENT_STATUS_FILTER_MAP[paymentFilter] !== undefined) {
        params.set("payment_status", String(PAYMENT_STATUS_FILTER_MAP[paymentFilter]));
      }

      if (
        subscriptionFilter !== "All" &&
        SUBSCRIPTION_STATUS_FILTER_MAP[subscriptionFilter]
      ) {
        params.set("subscription_status", SUBSCRIPTION_STATUS_FILTER_MAP[subscriptionFilter]);
      }

      const response = await handleApiCall(
        "get",
        `${endpoints.getTransactionList}?${params.toString()}`
      );

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const payload = response?.data?.data;
        setData(parseTransactionList(payload));
        setTotalPages(
          Number(
            payload?.pagination?.total_pages ||
              payload?.pagination?.totalPages ||
              payload?.totalPages ||
              1
          ) || 1
        );
      } else {
        setData([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setData([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, paymentFilter, subscriptionFilter]);

  useEffect(() => {
    getTransactionList();
  }, [getTransactionList]);

  useEffect(() => {
    const listState = location.state?.listState;
    if (!listState || restoredRef.current) return;
    restoredRef.current = true;
    applyListState(listState, {
      setPage,
      setSearch,
      setFilter: setPaymentFilter,
    });
    if (listState.subscriptionFilter) {
      setSubscriptionFilter(listState.subscriptionFilter);
    }
    if (listState.paymentFilter) {
      setPaymentFilter(listState.paymentFilter);
    }
  }, [location.state]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (paymentFilterRef.current && !paymentFilterRef.current.contains(event.target)) {
        setShowPaymentFilter(false);
      }
      if (
        subscriptionFilterRef.current &&
        !subscriptionFilterRef.current.contains(event.target)
      ) {
        setShowSubscriptionFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const listNavState = {
    page,
    search,
    paymentFilter,
    subscriptionFilter,
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Transaction Management</h3>
            </div>
            <div className="mb-4 px-0 py-2">
              <div className="card-heading d-flex align-items-center mb-3 pb-2">
                <div className="d-flex align-items-center col-md-12 px-0">
                  <form
                    className="col-md-4 px-0"
                    onSubmit={(e) => {
                      e.preventDefault();
                      getTransactionList();
                    }}
                  >
                    <div className="form-group my-0">
                      <input
                        className="form-control search-icon left-icon border"
                        placeholder="Search by restaurant, plan or subscription"
                        value={search}
                        onChange={handleSearch}
                      />
                    </div>
                  </form>

                  <div className="d-flex align-items-center ml-auto">
                    <div
                      ref={paymentFilterRef}
                      className="d-flex align-items-center mr-3 position-relative"
                    >
                      <FilterTrigger
                        active={paymentFilter !== "All"}
                        onClick={() => {
                          setShowPaymentFilter((prev) => !prev);
                          setShowSubscriptionFilter(false);
                        }}
                      >
                        {showPaymentFilter && (
                          <FilterOptionsMenu
                            options={PAYMENT_STATUS_FILTER_OPTIONS}
                            value={paymentFilter}
                            style={{ right: 0, top: "calc(100% + 8px)" }}
                            onSelect={(option) => {
                              setPaymentFilter(option);
                              setShowPaymentFilter(false);
                              setPage(1);
                            }}
                          />
                        )}
                      </FilterTrigger>
                      <span
                        className="ml-2 text-white"
                        role="button"
                        onClick={() => {
                          setShowPaymentFilter((prev) => !prev);
                          setShowSubscriptionFilter(false);
                        }}
                      >
                        Payment Status
                      </span>
                    </div>

                    <div
                      ref={subscriptionFilterRef}
                      className="d-flex align-items-center position-relative"
                    >
                      <FilterTrigger
                        active={subscriptionFilter !== "All"}
                        onClick={() => {
                          setShowSubscriptionFilter((prev) => !prev);
                          setShowPaymentFilter(false);
                        }}
                      >
                        {showSubscriptionFilter && (
                          <FilterOptionsMenu
                            options={SUBSCRIPTION_STATUS_FILTER_OPTIONS}
                            value={subscriptionFilter}
                            style={{ right: 0, top: "calc(100% + 8px)" }}
                            onSelect={(option) => {
                              setSubscriptionFilter(option);
                              setShowSubscriptionFilter(false);
                              setPage(1);
                            }}
                          />
                        )}
                      </FilterTrigger>
                      <span
                        className="ml-2 text-white"
                        role="button"
                        onClick={() => {
                          setShowSubscriptionFilter((prev) => !prev);
                          setShowPaymentFilter(false);
                        }}
                      >
                        Subscription Status
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="table-responsive px-0">
                  <table className="table table-striped trim">
                    <thead>
                      <tr>
                        <th width="80">S.No.</th>
                        <th width="150">Restaurant Name</th>
                        <th>Subscription</th>
                        <th>Plan Type</th>
                        <th>Amount</th>
                        <th>Billing Cycle</th>
                        <th>Payment Status</th>
                        <th>Subscription Status</th>
                        <th>Start Date</th>
                        <th>Renewal Date</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="11" className="text-center py-4">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">{UI_CONSTANTS.BUTTON_LABELS.LOADING}</span>
                            </div>
                          </td>
                        </tr>
                      ) : data.length === 0 ? (
                        <tr>
                          <td colSpan="11" className="text-center py-4">
                            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                          </td>
                        </tr>
                      ) : (
                        data.map((item, index) => (
                          <tr key={item._id || item.id || index}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td>{getRestaurantName(item)}</td>
                            <td>{getSubscriptionName(item)}</td>
                            <td>{getPlanType(item)}</td>
                            <td>{formatAmount(item)}</td>
                            <td>{formatBillingCycle(item)}</td>
                            <td>{getPaymentStatusLabel(item.payment_status)}</td>
                            <td>
                              {getSubscriptionStatusLabel(
                                item.subscription_status || item.status
                              )}
                            </td>
                            <td>{formatDate(item.start_date || item.startDate)}</td>
                            <td>
                              {formatDate(
                                item.expiry_date ||
                                  item.renewal_date ||
                                  item.renewalDate ||
                                  item.end_date ||
                                  item.endDate
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center justify-content-center">
                                <Link
                                  to={`/transaction-management/subscription-view/${item._id || item.id}`}
                                  state={{
                                    from: "/transaction-management",
                                    listState: listNavState,
                                  }}
                                  className="mx-2"
                                >
                                  <img src={viewImage} alt="View" />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TransactionManagement;
